import type { FC, ReactNode } from 'react';

import type { ConversationChromeVariant } from './ConversationMessageCard';
import type { MessageMetaItem } from './MessageMeta';
import { MessageMeta } from './MessageMeta';

import {
  copyAffordance,
  investigation,
  investigationBelow,
  investigationBody,
  investigationDetail,
  investigationDuration,
  investigationError,
  investigationExtras,
  investigationHead,
  investigationHeadMain,
  investigationHeadMainButton,
  investigationIcon,
  investigationInteractive,
  investigationLabel,
  investigationLabelLoading,
  turn,
  turnAgent,
  turnAgentChat,
} from '../../internal/components/conversation/ConversationChrome.css';

export interface InvestigationRowProps {
  icon?: ReactNode;
  label: string;
  /** Command / one-line preview shown beside the label. */
  detail?: string;
  durationLabel?: string;
  isLoading?: boolean;
  isError?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
  children?: ReactNode;
  /** Status chip inside the toggle so it expands/collapses with the row. */
  status?: ReactNode;
  /** Sibling controls outside the toggle (e.g. span icon buttons). */
  headerExtra?: ReactNode;
  /** Supplemental action rendered with the row's metadata caption. */
  footer?: ReactNode;
  meta?: MessageMetaItem[];
  metaTitle?: string;
  className?: string;
  /**
   * `transcript` (default) keeps the agent gutter. `chat` flushes the row so
   * it matches Vega's compact message column.
   */
  variant?: ConversationChromeVariant;
  /** Hide the caption row until the turn is hovered or focused. Pair with `copyHoverGroup` on `className`. */
  hoverRevealCaption?: boolean;
}

const HeadMain: FC<{
  icon?: ReactNode;
  label: string;
  detail?: string;
  durationLabel?: string;
  isLoading: boolean;
  status?: ReactNode;
}> = ({ icon, label, detail, durationLabel, isLoading, status }) => (
  <>
    {icon ? <span className={investigationIcon}>{icon}</span> : null}
    <span className={[investigationLabel, isLoading ? investigationLabelLoading : undefined].filter(Boolean).join(' ')}>
      {label}
    </span>
    {detail ? (
      <span className={investigationDetail} title={detail}>
        {detail}
      </span>
    ) : null}
    {durationLabel ? <span className={investigationDuration}>{durationLabel}</span> : null}
    {status}
  </>
);

/**
 * Compact tool / investigation row shared by Vega chat and span Read transcripts.
 * Expand, error, and loading are optional — Figma shows the collapsed chrome only.
 * `status` lives inside the toggle; `headerExtra` stays beside it so nested buttons
 * remain valid HTML.
 */
export const InvestigationRow: FC<InvestigationRowProps> = ({
  icon,
  label,
  detail,
  durationLabel,
  isLoading = false,
  isError = false,
  expanded = false,
  onToggle,
  children,
  status,
  headerExtra,
  footer,
  meta,
  metaTitle,
  className,
  variant = 'transcript',
  hoverRevealCaption,
}) => {
  const interactive = Boolean(onToggle);
  const mainProps = { icon, label, detail, durationLabel, isLoading, status };
  const turnClass = variant === 'chat' ? turnAgentChat : turnAgent;

  return (
    <div className={[turn, turnClass, className].filter(Boolean).join(' ')}>
      <div
        className={[
          investigation,
          isError ? investigationError : undefined,
          interactive ? investigationInteractive : undefined,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <div className={investigationHead}>
          {interactive ? (
            <button type="button" className={investigationHeadMainButton} aria-expanded={expanded} onClick={onToggle}>
              <HeadMain {...mainProps} />
            </button>
          ) : (
            <div className={investigationHeadMain}>
              <HeadMain {...mainProps} />
            </div>
          )}
          {headerExtra ? <div className={investigationExtras}>{headerExtra}</div> : null}
        </div>
        {expanded && children ? <div className={investigationBody}>{children}</div> : null}
      </div>
      {meta || footer ? (
        <div
          className={[investigationBelow, hoverRevealCaption ? copyAffordance : undefined].filter(Boolean).join(' ')}
        >
          {meta ? <MessageMeta items={meta} title={metaTitle} /> : null}
          {footer}
        </div>
      ) : null}
    </div>
  );
};
